import { Scene } from "three"
import type ThreeJSApp from "../app"
import Loader from "../base/loader/loader"
import AvatarManager from "../avatar/avatar-manager"
import { AvatarType } from "../avatar/common/player/type"
import EventEmitter from "events"

export default class World extends EventEmitter {
  root: ThreeJSApp
  scene: Scene
  loader: Loader
  avatarManager: AvatarManager
  isReady: boolean = false
  currentAvatarType: AvatarType | null = null // 초기값을 null로 변경

  constructor(root: ThreeJSApp) {
    super() // EventEmitter 초기화
    this.root = root
    this.scene = this.root.scene
    this.loader = this.root.loader
    this.avatarManager = new AvatarManager(this.root)

    // 자동 아바타 로딩 제거 - 이제 selectAvatar 호출로만 로딩됩니다
    this.setupAvatarManagerEvents()
  }

  /**
   * 아바타 매니저 이벤트를 설정합니다.
   */
  private setupAvatarManagerEvents() {


    // 아바타 매니저 이벤트 설정
    this.avatarManager.on('ready', (avatarType) => {
      this.isReady = true
      console.log(`✅ World: 아바타 ${avatarType} 로드 완료`)
    })

    // 로딩 진행률 이벤트를 Root로 전달
    this.avatarManager.on('loadProgress', (progressData) => {
      this.emit('loadProgress', progressData); // World에서 직접 emit
      this.root.emit('loadProgress', progressData); // Root에도 전달
    })
  }

  /**
   * 아바타를 선택하고 로드합니다.
   * @param avatarType 선택할 아바타 타입
   */
  async selectAvatar(avatarType: AvatarType): Promise<void> {
    try {
      
      this.isReady = false
      this.currentAvatarType = avatarType

      await this.avatarManager.selectAvatar(avatarType)

      // 렌더링 업데이트 요청
      this.root.markNeedsUpdate()
    } catch (error) {
      console.error(`❌ World: 아바타 ${avatarType} 로드 실패:`, error)
      throw error
    }
  }

  /**
   * 현재 아바타를 가져옵니다.
   */
  getCurrentAvatar() {
    return this.avatarManager.getCurrentAvatar()
  }

  /**
   * 현재 아바타 타입을 가져옵니다.
   */
  getCurrentAvatarType(): AvatarType | null {
    return this.currentAvatarType
  }

  /**
   * 사용 가능한 아바타 타입 목록을 가져옵니다.
   */
  getAvailableAvatarTypes(): AvatarType[] {
    return this.avatarManager.getAvailableAvatarTypes()
  }

  /**
   * 애니메이션을 재생합니다.
   * @param name 시나리오 이름
   */
  play(name: string): void {
    this.avatarManager.play(name)
  }

  /**
   * 애니메이션을 중지합니다.
   */
  stop(): void {
    this.avatarManager.stop()
  }

  /**
   * 립싱크를 재생합니다.
   * @param blendshapeList 블렌드쉐이프 데이터
   * @param duration 지속 시간
   */
  playLipSync(blendshapeList: number[][], duration?: number): void {
    this.avatarManager.playLipSync(blendshapeList, duration)
  }

  /**
   * 립싱크를 중지합니다.
   */
  stopLipSync(): void {
    this.avatarManager.stopLipSync()
  }

  /**
   * 시나리오 종료 콜백을 설정합니다.
   * @param callback 콜백 함수
   */
  onEndScenario(callback: (...args: unknown[]) => void): World {
    this.avatarManager.onEndScenario(callback)
    return this
  }

  update(delta: number) {
    if (!this.isReady) return
    this.avatarManager.update(delta)
  }

  destroy() {
    console.log('🌍 World: World 인스턴스 정리 시작');
    
    // 아바타 매니저의 이벤트 리스너 정리
    this.avatarManager.removeAllListeners();
    
    // 아바타 매니저 정리
    this.avatarManager.destroy();
    
    // World 자체의 이벤트 리스너 정리
    this.removeAllListeners();
    
    // 상태 초기화
    this.isReady = false;
    this.currentAvatarType = null;
    
    console.log('🌍 World: World 인스턴스 정리 완료');
  }
}